/**
 * The workspace's master-detail shell, shared by Queue and History.
 *
 * Selecting an Alert must cost the operator nothing: the list stays mounted and
 * keeps its scroll position, filters, page and loaded rows — the detail region
 * simply appears beside it. That is why selection is an id held in the store
 * rather than a route change or a modal.
 *
 * List and detail scroll independently, and the page itself never scrolls
 * horizontally: the two regions are a min-width-0 flex pair, so the detail
 * column shrinks instead of pushing the layout wide.
 */
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import { AlertsTable } from "./alerts-table";
import { AlertListSkeleton, AlertsErrorState } from "./alerts-list-states";
import type { AlertRow } from "@/alerts/types/alerts-workspace";

interface AlertMasterDetailProps {
  rows: AlertRow[];
  /** Switches the list's trailing column: waiting age vs closed time. */
  mode: "queue" | "history";
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  /** Filters and scope controls, pinned above the scrolling list. */
  toolbar: ReactNode;
  /** Full-width notice above the rows (e.g. the truncated-results warning). */
  banner?: ReactNode;
  /** Pagination, pinned below the scrolling list. */
  footer?: ReactNode;
  /** Rendered when there are no rows and nothing is loading or failing. */
  emptyState: ReactNode;
  /** First load only — a background refresh keeps the previous rows visible. */
  isInitialLoading: boolean;
  error: unknown;
  onRetry: () => void;
  /**
   * Builds the detail region for the selected row. Receives `onNext` so the
   * pane can advance down the list without the operator returning to it —
   * working a queue is a sequence, not a series of round trips.
   */
  renderDetail: (args: { row: AlertRow; onClose: () => void; onNext?: () => void }) => ReactNode;
  /** The queue's server clock, so row ages don't depend on the browser's. */
  asOf?: string;
}

export const AlertMasterDetail = ({
  rows,
  mode,
  selectedId,
  onSelect,
  toolbar,
  banner,
  footer,
  emptyState,
  isInitialLoading,
  error,
  onRetry,
  renderDetail,
  asOf,
}: AlertMasterDetailProps) => {
  const { t } = useTranslation("alerts");
  const rowRefs = useRef(new Map<number, HTMLTableRowElement>());
  // The row that opened the detail, so closing can hand focus back to it even
  // after the selection state has already been cleared.
  const originRowIdRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const registerRow = useCallback((id: number, el: HTMLTableRowElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  const select = useCallback(
    (id: number | null) => {
      if (id != null) originRowIdRef.current = id;
      onSelect(id);
    },
    [onSelect],
  );

  const close = useCallback(() => {
    const origin = originRowIdRef.current;
    onSelect(null);
    // Focus returns to the originating row where it still exists — on the queue
    // it may have been reviewed away while the detail was open, in which case
    // the list container takes focus rather than losing it to the document.
    const target = origin != null ? rowRefs.current.get(origin) : null;
    (target ?? listRef.current)?.focus?.();
    originRowIdRef.current = null;
  }, [onSelect]);

  // Arrow keys step through the list; Escape closes the detail. Bound on the
  // list container so it never competes with typing in the filter controls.
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && selectedId != null) {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    if (rows.length === 0) return;
    event.preventDefault();
    const index = rows.findIndex((row) => row.id === selectedId);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const nextIndex =
      index === -1 ? (step === 1 ? 0 : rows.length - 1) : Math.min(rows.length - 1, Math.max(0, index + step));
    const next = rows[nextIndex];
    if (!next) return;
    select(next.id);
    rowRefs.current.get(next.id)?.focus();
  };

  // A selection that no longer exists (reviewed away, filtered out, or a page
  // change) must not leave the detail region showing a stale alert.
  useEffect(() => {
    if (selectedId == null || isInitialLoading) return;
    if (!rows.some((row) => row.id === selectedId)) onSelect(null);
  }, [rows, selectedId, isInitialLoading, onSelect]);

  const showEmpty = !isInitialLoading && !error && rows.length === 0;
  const selectedIndex = rows.findIndex((row) => row.id === selectedId);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const nextRow = selectedIndex >= 0 ? rows[selectedIndex + 1] : undefined;

  return (
    <div className="flex h-full min-h-0 min-w-0 gap-4 p-4">
      <section
        className={cn(
          "flex min-h-0 min-w-0 flex-col rounded-lg border border-border bg-background",
          selectedId != null ? "flex-1 basis-1/2" : "flex-1",
        )}
        aria-label={t("masterDetail.listLabel")}
      >
        <div className="shrink-0 border-b border-border">{toolbar}</div>
        {banner}
        <div
          ref={listRef}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
        >
          {error ? (
            <AlertsErrorState error={error} onRetry={onRetry} />
          ) : isInitialLoading ? (
            <AlertListSkeleton />
          ) : showEmpty ? (
            emptyState
          ) : (
            <AlertsTable
              items={rows}
              mode={mode}
              asOf={asOf}
              selectedId={selectedId}
              onSelect={(id) => select(id === selectedId ? null : id)}
              registerRow={registerRow}
            />
          )}
        </div>
        {footer && <div className="shrink-0 border-t border-border">{footer}</div>}
      </section>

      {selectedRow && (
        <aside
          className="flex min-h-0 w-[420px] max-w-[45%] shrink-0 flex-col overflow-y-auto rounded-lg border border-border bg-background"
          aria-label={t("masterDetail.detailLabel")}
        >
          {renderDetail({
            row: selectedRow,
            onClose: close,
            onNext: nextRow ? () => select(nextRow.id) : undefined,
          })}
        </aside>
      )}
    </div>
  );
};

